import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Shield, Users, GraduationCap, Warning, ChartLine,
  Check, TrendDown, TrendUp, Minus, CaretRight, Bell, Gear,
  MagnifyingGlass, User, SignOut, House
} from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlatformDemo = () => {
  const [activeModule, setActiveModule] = useState("compliance");
  const [complianceData, setComplianceData] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);
  const [incidentData, setIncidentData] = useState(null);
  const [trainingData, setTrainingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDemoData();
  }, []);

  const fetchDemoData = async () => {
    try {
      const [compliance, onboarding, incidents, training] = await Promise.all([
        axios.get(`${API}/demo/compliance-score`),
        axios.get(`${API}/demo/onboarding-progress`),
        axios.get(`${API}/demo/incidents`),
        axios.get(`${API}/demo/training`)
      ]);
      setComplianceData(compliance.data);
      setOnboardingData(onboarding.data);
      setIncidentData(incidents.data);
      setTrainingData(training.data);
    } catch (err) {
      console.error("Failed to fetch demo data:", err);
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    { id: "compliance", icon: Shield, label: "Compliance" },
    { id: "onboarding", icon: Users, label: "Onboarding" },
    { id: "training", icon: GraduationCap, label: "Training" },
    { id: "incidents", icon: Warning, label: "Incidents" },
  ];

  const getTrendIcon = (trend) => {
    if (trend === "up") return <TrendUp weight="bold" className="w-4 h-4 text-green-500" />;
    if (trend === "down") return <TrendDown weight="bold" className="w-4 h-4 text-red-500" />;
    return <Minus weight="bold" className="w-4 h-4 text-muted-foreground" />;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: "bg-red-500",
      high: "bg-orange-500",
      medium: "bg-yellow-500",
      low: "bg-green-500"
    };
    return colors[severity] || "bg-gray-500";
  };

  const getStatusColor = (status) => {
    const colors = {
      "on-track": "text-green-600 bg-green-500/10",
      "completing": "text-blue-600 bg-blue-500/10",
      "at-risk": "text-orange-600 bg-orange-500/10",
      "overdue": "text-red-600 bg-red-500/10"
    };
    return colors[status] || "text-muted-foreground bg-muted";
  };

  return (
    <div data-testid="platform-demo-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background" data-testid="demo-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge-accent mb-6 inline-block">Interactive Demo</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
              Experience the Noble Platform
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Explore our AI-powered dashboards and see how Noble transforms hospice 
              compliance, onboarding, training, and incident management.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Dashboard Demo */}
      <section className="py-12 md:py-16" data-testid="dashboard-demo">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-lg">
            {/* Dashboard Header */}
            <div className="bg-foreground px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                    <Shield weight="fill" className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold">Noble Platform</span>
                </div>
                <div className="hidden md:flex items-center gap-1 bg-white/10 rounded-lg px-3 py-2">
                  <MagnifyingGlass className="w-4 h-4 text-white/60" />
                  <input 
                    type="text" 
                    placeholder="Search..." 
                    className="bg-transparent text-white text-sm placeholder:text-white/40 outline-none w-40"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button className="text-white/60 hover:text-white transition-colors">
                  <Bell weight="fill" className="w-5 h-5" />
                </button>
                <button className="text-white/60 hover:text-white transition-colors">
                  <Gear weight="fill" className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 pl-4 border-l border-white/10">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <User weight="fill" className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white text-sm hidden md:block">Demo User</span>
                </div>
              </div>
            </div>

            <div className="flex">
              {/* Sidebar */}
              <div className="w-16 md:w-64 bg-muted/50 border-r border-border p-4 hidden sm:block">
                <nav className="space-y-2">
                  <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors">
                    <House weight="duotone" className="w-5 h-5" />
                    <span className="hidden md:block text-sm">Home</span>
                  </Link>
                  {modules.map((module) => (
                    <button
                      key={module.id}
                      onClick={() => setActiveModule(module.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        activeModule === module.id
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                      data-testid={`module-${module.id}`}
                    >
                      <module.icon weight={activeModule === module.id ? "fill" : "duotone"} className="w-5 h-5" />
                      <span className="hidden md:block text-sm">{module.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Main Content */}
              <div className="flex-1 p-6 md:p-8 min-h-[600px]">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="spinner" />
                  </div>
                ) : (
                  <>
                    {/* Compliance Dashboard */}
                    {activeModule === "compliance" && complianceData && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        data-testid="compliance-dashboard"
                      >
                        <div className="flex items-center justify-between mb-8">
                          <div>
                            <h2 className="text-2xl font-semibold text-foreground">Compliance Dashboard</h2>
                            <p className="text-muted-foreground">AI-powered compliance monitoring and risk assessment</p>
                          </div>
                          <span className="badge-accent">Live Demo</span>
                        </div>

                        {/* Score Overview */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                          <div className="md:col-span-1 bg-gradient-to-br from-primary/10 to-accent/10 border border-border rounded-xl p-6 flex flex-col items-center justify-center">
                            <div className="text-6xl font-bold text-primary font-display">{complianceData.overall_score}</div>
                            <div className="text-sm text-muted-foreground mt-2">Overall Score</div>
                            <div className="flex items-center gap-1 mt-2 text-green-600 text-sm">
                              <TrendUp weight="bold" className="w-4 h-4" />
                              <span>+3 this month</span>
                            </div>
                          </div>
                          <div className="md:col-span-3 bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">Category Scores</h3>
                            <div className="space-y-4">
                              {complianceData.categories.map((cat, i) => (
                                <div key={i} className="flex items-center gap-4">
                                  <span className="text-sm text-muted-foreground w-32">{cat.name}</span>
                                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-primary rounded-full transition-all duration-500"
                                      style={{ width: `${cat.score}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-semibold w-10">{cat.score}%</span>
                                  {getTrendIcon(cat.trend)}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Risk Items & Recent Audits */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">Active Risk Items</h3>
                            <div className="space-y-4">
                              {complianceData.risk_items.map((item) => (
                                <div key={item.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                                  <div className={`w-2 h-2 rounded-full mt-2 ${getSeverityColor(item.severity)}`} />
                                  <div className="flex-1">
                                    <p className="text-sm text-foreground">{item.description}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Action: {item.action}</p>
                                  </div>
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    item.severity === "high" ? "bg-orange-500/10 text-orange-600" :
                                    item.severity === "medium" ? "bg-yellow-500/10 text-yellow-600" :
                                    "bg-green-500/10 text-green-600"
                                  }`}>
                                    {item.severity}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">Recent Audits</h3>
                            <div className="space-y-4">
                              {complianceData.recent_audits.map((audit, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                  <div>
                                    <p className="text-sm font-medium text-foreground">{audit.type}</p>
                                    <p className="text-xs text-muted-foreground">{audit.date}</p>
                                  </div>
                                  <div className={`text-lg font-bold ${audit.score >= 90 ? "text-green-600" : audit.score >= 80 ? "text-yellow-600" : "text-red-600"}`}>
                                    {audit.score}%
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Onboarding Dashboard */}
                    {activeModule === "onboarding" && onboardingData && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        data-testid="onboarding-dashboard"
                      >
                        <div className="flex items-center justify-between mb-8">
                          <div>
                            <h2 className="text-2xl font-semibold text-foreground">Onboarding Dashboard</h2>
                            <p className="text-muted-foreground">Role-based onboarding with CMS CoP alignment</p>
                          </div>
                          <span className="badge-accent">Live Demo</span>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-primary">{onboardingData.active_onboarding}</div>
                            <div className="text-sm text-muted-foreground">Active Onboarding</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-green-600">{onboardingData.completed_this_month}</div>
                            <div className="text-sm text-muted-foreground">Completed This Month</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-accent">{onboardingData.average_completion_days}</div>
                            <div className="text-sm text-muted-foreground">Avg. Days to Complete</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-foreground">100%</div>
                            <div className="text-sm text-muted-foreground">CoP Compliance</div>
                          </div>
                        </div>

                        {/* Staff Progress & Milestones */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">Staff Progress</h3>
                            <div className="space-y-4">
                              {onboardingData.staff.map((person, i) => (
                                <div key={i} className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                    <User weight="fill" className="w-5 h-5 text-muted-foreground" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm font-medium text-foreground">{person.name}</span>
                                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(person.status)}`}>
                                        {person.status}
                                      </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mb-2">{person.role}</div>
                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-primary rounded-full"
                                        style={{ width: `${person.progress}%` }}
                                      />
                                    </div>
                                  </div>
                                  <span className="text-sm font-semibold w-12 text-right">{person.progress}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">Milestone Completion</h3>
                            <div className="space-y-4">
                              {onboardingData.milestones.map((milestone, i) => (
                                <div key={i}>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-foreground">{milestone.name}</span>
                                    <span className="text-sm font-semibold">{milestone.completion_rate}%</span>
                                  </div>
                                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${milestone.completion_rate === 100 ? "bg-green-500" : "bg-primary"}`}
                                      style={{ width: `${milestone.completion_rate}%` }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Training Dashboard */}
                    {activeModule === "training" && trainingData && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        data-testid="training-dashboard"
                      >
                        <div className="flex items-center justify-between mb-8">
                          <div>
                            <h2 className="text-2xl font-semibold text-foreground">Training Dashboard</h2>
                            <p className="text-muted-foreground">Adaptive training tied to audit findings</p>
                          </div>
                          <span className="badge-accent">Live Demo</span>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                          <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-xl p-5">
                            <div className="text-3xl font-bold text-green-600">{trainingData.compliance_rate}%</div>
                            <div className="text-sm text-muted-foreground">Compliance Rate</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-primary">{trainingData.courses_available}</div>
                            <div className="text-sm text-muted-foreground">Courses Available</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-foreground">{trainingData.staff_training_status.compliant}</div>
                            <div className="text-sm text-muted-foreground">Staff Compliant</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-orange-600">{trainingData.certifications_expiring_soon}</div>
                            <div className="text-sm text-muted-foreground">Certs Expiring Soon</div>
                          </div>
                        </div>

                        {/* Popular Courses & Deadlines */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">Popular Courses</h3>
                            <div className="space-y-4">
                              {trainingData.popular_courses.map((course, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-foreground">{course.name}</p>
                                    <p className="text-xs text-muted-foreground">{course.completions} completions</p>
                                  </div>
                                  <div className="flex items-center gap-1 text-accent">
                                    <span className="text-sm font-semibold">{course.rating}</span>
                                    <span className="text-xs">★</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">Upcoming Deadlines</h3>
                            <div className="space-y-4">
                              {trainingData.upcoming_deadlines.map((deadline, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                  <div>
                                    <p className="text-sm font-medium text-foreground">{deadline.course}</p>
                                    <p className="text-xs text-muted-foreground">{deadline.staff_count} staff affected</p>
                                  </div>
                                  <div className="text-sm text-orange-600 font-medium">{deadline.due_date}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Incidents Dashboard */}
                    {activeModule === "incidents" && incidentData && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        data-testid="incidents-dashboard"
                      >
                        <div className="flex items-center justify-between mb-8">
                          <div>
                            <h2 className="text-2xl font-semibold text-foreground">Incident Dashboard</h2>
                            <p className="text-muted-foreground">AI-powered incident tracking and root cause analysis</p>
                          </div>
                          <span className="badge-accent">Live Demo</span>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-foreground">{incidentData.total_this_month}</div>
                            <div className="text-sm text-muted-foreground">This Month</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-orange-600">{incidentData.pending_review}</div>
                            <div className="text-sm text-muted-foreground">Pending Review</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-primary">{incidentData.average_resolution_days}d</div>
                            <div className="text-sm text-muted-foreground">Avg. Resolution</div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="text-3xl font-bold text-green-600">{incidentData.qapi_summary.improvements_completed}</div>
                            <div className="text-sm text-muted-foreground">QAPI Improvements</div>
                          </div>
                        </div>

                        {/* Severity & Categories */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">By Severity</h3>
                            <div className="space-y-3">
                              {Object.entries(incidentData.by_severity).map(([severity, count]) => (
                                <div key={severity} className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-3 h-3 rounded-full ${getSeverityColor(severity)}`} />
                                    <span className="text-sm capitalize text-foreground">{severity}</span>
                                  </div>
                                  <span className="text-sm font-semibold">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">By Category</h3>
                            <div className="space-y-3">
                              {incidentData.by_category.map((cat, i) => (
                                <div key={i} className="flex items-center justify-between">
                                  <span className="text-sm text-foreground">{cat.category}</span>
                                  <span className="text-sm font-semibold">{cat.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="font-semibold text-foreground mb-4">QAPI Summary</h3>
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Trends Identified</span>
                                <span className="text-sm font-semibold text-foreground">{incidentData.qapi_summary.trends_identified}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Action Plans Active</span>
                                <span className="text-sm font-semibold text-foreground">{incidentData.qapi_summary.action_plans_active}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Improvements Completed</span>
                                <span className="text-sm font-semibold text-green-600">{incidentData.qapi_summary.improvements_completed}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Recent Incidents */}
                        <div className="bg-card border border-border rounded-xl p-6">
                          <h3 className="font-semibold text-foreground mb-4">Recent Incidents</h3>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">ID</th>
                                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Category</th>
                                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Severity</th>
                                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {incidentData.recent.map((incident, i) => (
                                  <tr key={i} className="border-b border-border/50 hover:bg-muted/50">
                                    <td className="py-3 px-4 text-sm font-medium text-primary">{incident.id}</td>
                                    <td className="py-3 px-4 text-sm text-foreground">{incident.date}</td>
                                    <td className="py-3 px-4 text-sm text-foreground">{incident.category}</td>
                                    <td className="py-3 px-4">
                                      <span className={`text-xs px-2 py-1 rounded-full ${
                                        incident.severity === "high" ? "bg-orange-500/10 text-orange-600" :
                                        incident.severity === "medium" ? "bg-yellow-500/10 text-yellow-600" :
                                        "bg-green-500/10 text-green-600"
                                      }`}>
                                        {incident.severity}
                                      </span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className={`text-xs px-2 py-1 rounded-full ${
                                        incident.status === "resolved" ? "bg-green-500/10 text-green-600" :
                                        "bg-blue-500/10 text-blue-600"
                                      }`}>
                                        {incident.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Module Selector */}
          <div className="sm:hidden flex justify-center gap-2 mt-4">
            {modules.map((module) => (
              <button
                key={module.id}
                onClick={() => setActiveModule(module.id)}
                className={`p-3 rounded-lg transition-colors ${
                  activeModule === module.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <module.icon weight={activeModule === module.id ? "fill" : "duotone"} className="w-5 h-5" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="demo-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-6">
              Ready to See It in Action?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Schedule a personalized demo with our team to see how Noble can transform 
              your hospice's compliance and operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 rounded-full font-medium transition-all hover:scale-105 flex items-center justify-center gap-2"
                data-testid="demo-cta-consultation"
              >
                Schedule Demo
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground h-14 px-8 rounded-full font-medium transition-all flex items-center justify-center gap-2"
                data-testid="demo-cta-services"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PlatformDemo;
